// frontend-react/src/components/BatchValidationModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Upload,
  Space,
  Spin,
  Alert,
  Table,
  Typography,
  Select,
} from 'antd';
import {
  InboxOutlined,
  PlusOutlined,
  CloseOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  ExperimentOutlined,
  StarFilled,
  ReloadOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import type { BatchGroupResult, BatchValidationResult } from '../services/validationService';
import {
  batchValidate,
  downloadBatchGroupFile,
  downloadBatchCombinedExcel,
} from '../services/validationService';
import { loadRuleSets } from '../services/ruleService';
import type { RuleSet } from '../types/rule';

const { Text } = Typography;

const CN_NUMS = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
const MAX_GROUPS = 10;

type Step = 'idle' | 'processing' | 'done' | 'error';

interface GroupItem {
  legalDoc: File | null;
  originalFile: File | null;
}

interface GroupMeta {
  legalName: string;
  originalName: string;
}

interface BatchValidationModalProps {
  visible: boolean;
  onClose: () => void;
  userPreferredRuleSetId?: number | null;
}

const BTN_STYLE: React.CSSProperties = {
  background: '#6B7B5A',
  borderColor: '#6B7B5A',
  color: '#fff',
};

const BatchValidationModal: React.FC<BatchValidationModalProps> = ({
  visible,
  onClose,
  userPreferredRuleSetId = null,
}) => {
  const [step, setStep] = useState<Step>('idle');
  const [groups, setGroups] = useState<GroupItem[]>([{ legalDoc: null, originalFile: null }]);
  const [groupMetas, setGroupMetas] = useState<GroupMeta[]>([]);
  const [result, setResult] = useState<BatchValidationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([]);
  const [selectedRuleSetId, setSelectedRuleSetId] = useState<number | null>(null);
  const [ruleLoading, setRuleLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setRuleLoading(true);
    loadRuleSets()
      .then(data => {
        setRuleSets(data);
        // 自動選取：個人偏好優先，次選公司預設
        setSelectedRuleSetId(prev => {
          if (prev !== null) return prev;
          if (
            userPreferredRuleSetId &&
            userPreferredRuleSetId > 0 &&
            data.some(r => r.id === userPreferredRuleSetId)
          ) {
            return userPreferredRuleSetId;
          }
          const companyDefault = data.find(r => r.is_default);
          return companyDefault?.id ?? null;
        });
      })
      .finally(() => setRuleLoading(false));
  }, [visible, userPreferredRuleSetId]);

  const reset = () => {
    setStep('idle');
    setGroups([{ legalDoc: null, originalFile: null }]);
    setGroupMetas([]);
    setResult(null);
    setErrorMessage('');
    setSelectedRuleSetId(null);
  };

  const handleClose = () => {
    if (step === 'processing') return;
    reset();
    onClose();
  };

  const addGroup = () => {
    if (groups.length >= MAX_GROUPS) return;
    setGroups(prev => [...prev, { legalDoc: null, originalFile: null }]);
  };

  const removeGroup = (idx: number) => {
    setGroups(prev => prev.filter((_, i) => i !== idx));
  };

  const setFile = (groupIdx: number, field: 'legalDoc' | 'originalFile', file: File) => {
    setGroups(prev => prev.map((g, i) => (i === groupIdx ? { ...g, [field]: file } : g)));
  };

  const clearFile = (groupIdx: number, field: 'legalDoc' | 'originalFile') => {
    setGroups(prev => prev.map((g, i) => (i === groupIdx ? { ...g, [field]: null } : g)));
  };

  const allReady = groups.every(g => g.legalDoc && g.originalFile);

  const handleSubmit = async () => {
    // 記錄每組的檔名供結果頁顯示
    setGroupMetas(
      groups.map(g => ({
        legalName: g.legalDoc?.name ?? '',
        originalName: g.originalFile?.name ?? '',
      }))
    );
    setStep('processing');
    try {
      const payload = groups.map((g, i) => ({
        legalDoc: g.legalDoc!,
        originalFile: g.originalFile!,
        name: `測試${CN_NUMS[i]}`,
      }));
      const res = await batchValidate(payload, selectedRuleSetId);
      setResult(res);
      setStep('done');
    } catch (err: any) {
      setErrorMessage(err?.response?.data?.detail || err?.message || '批次驗測失敗，請稍後再試');
      setStep('error');
    }
  };

  const handleDownloadGroup = async (
    batchId: string,
    groupIdx: number,
    fileType: 'word' | 'excel'
  ) => {
    const meta = groupMetas[groupIdx];
    const baseName = meta?.originalName
      ? meta.originalName.replace(/\.[^/.]+$/, '') // 去掉副檔名
      : `測試${CN_NUMS[groupIdx]}`;
    const ext = fileType === 'word' ? 'docx' : 'xlsx';
    const downloadName = `validation_${baseName}.${ext}`;
    try {
      await downloadBatchGroupFile(batchId, groupIdx, fileType, downloadName);
    } catch {
      // 單次下載失敗不影響其他
    }
  };

  const handleDownloadCombined = async () => {
    if (!result) return;
    const n = result.groups.length;
    const ruleSet = ruleSets.find(r => r.id === selectedRuleSetId)
      ?? ruleSets.find(r => r.is_default);
    const ruleName = ruleSet?.rule_set_name ?? '預設規則集';
    const safeRuleName = ruleName.replace(/[/\\:*?"<>|]/g, '_');
    const filename = `validation_batch_${n}組_${safeRuleName}.xlsx`;
    try {
      await downloadBatchCombinedExcel(result.batch_id, filename);
    } catch {
      // silent
    }
  };

  // ── 上傳小元件（扁平版） ──
  const FileSlot = ({
    label,
    accept,
    hint,
    file,
    onFile,
    onClear,
  }: {
    label: string;
    accept: string;
    hint: string;
    file: File | null;
    onFile: (f: File) => void;
    onClear: () => void;
  }) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      {!file ? (
        <Upload
          accept={accept}
          maxCount={1}
          showUploadList={false}
          beforeUpload={f => {
            onFile(f);
            return false;
          }}
          style={{ display: 'block' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              border: '1px dashed #d9b96c',
              borderRadius: 8,
              background: '#fffdf0',
              cursor: 'pointer',
            }}
          >
            <InboxOutlined style={{ fontSize: 18, color: '#6B7B5A', flexShrink: 0 }} />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: 500, color: '#374151', lineHeight: 1.3 }}>{label}</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>{hint}</div>
            </div>
          </div>
        </Upload>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 12px',
            background: '#f6f8f4',
            border: '1px solid #b7c9a8',
            borderRadius: 8,
          }}
        >
          <PaperClipOutlined style={{ color: '#6B7B5A', flexShrink: 0 }} />
          <span style={{ flex: 1, color: '#374151', wordBreak: 'break-all', lineHeight: 1.4 }}>
            {file.name}
          </span>
          <Button type='text' size='small' icon={<CloseOutlined />} onClick={onClear} />
        </div>
      )}
    </div>
  );

  // ── idle ──
  const renderIdle = () => (
    <Space direction='vertical' style={{ width: '100%' }} size='middle'>
      {/* 規則集選擇 */}
      <Select
        style={{ width: '100%' }}
        placeholder='使用預設規則集（不選則套用公司預設）'
        allowClear
        loading={ruleLoading}
        value={selectedRuleSetId}
        onChange={val => setSelectedRuleSetId(val ?? null)}
      >
        {/* 個人偏好置頂 */}
        {(() => {
          if (!userPreferredRuleSetId) return null;
          const preferred = ruleSets.find(r => r.id === userPreferredRuleSetId);
          if (!preferred) return null;
          return (
            <>
              <Select.Option key={`preferred-pinned-${preferred.id}`} value={preferred.id}>
                <StarFilled style={{ color: '#1677ff', marginRight: 6 }} />
                {preferred.rule_set_name}
                <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                  ({preferred.rules_count} 條規則)
                </span>
                <span style={{ color: '#1677ff', marginLeft: 8, fontSize: 12 }}>我的偏好</span>
              </Select.Option>
              <Select.Option
                key='preferred-divider'
                value='__divider__'
                disabled
                style={{ padding: 0, minHeight: 0 }}
              >
                <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px -12px' }} />
              </Select.Option>
            </>
          );
        })()}
        {/* 公司預設置頂：若不同於個人偏好則在第二位顯示 */}
        {(() => {
          const companyDefault = ruleSets.find(r => r.is_default && !r.is_legaltech_library);
          if (!companyDefault || companyDefault.id === userPreferredRuleSetId) return null;
          return (
            <>
              <Select.Option
                key={`company-default-pinned-${companyDefault.id}`}
                value={companyDefault.id}
              >
                <StarFilled style={{ color: '#faad14', marginRight: 6 }} />
                {companyDefault.rule_set_name}
                <span style={{ color: '#52c41a', marginLeft: 8, fontSize: 12 }}>預設</span>
                <span style={{ color: '#9ca3af', marginLeft: 4, fontSize: 12 }}>
                  （{companyDefault.rules_count} 條）
                </span>
              </Select.Option>
              <Select.Option
                key='company-default-divider'
                value='__company_default_divider__'
                disabled
                style={{ padding: 0, minHeight: 0 }}
              >
                <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px -12px' }} />
              </Select.Option>
            </>
          );
        })()}
        {/* 其餘規則集（已置頂的個人偏好與公司預設不重複顯示） */}
        {ruleSets
          .filter(rs => {
            const companyDefault = ruleSets.find(r => r.is_default && !r.is_legaltech_library);
            return rs.id !== userPreferredRuleSetId && rs.id !== companyDefault?.id;
          })
          .map(rs => (
            <Select.Option key={rs.id} value={rs.id}>
              {rs.is_default && !rs.is_legaltech_library && (
                <StarFilled style={{ color: '#faad14', marginRight: 6 }} />
              )}
              {rs.is_legaltech_library && (
                <img
                  src='/images/robot-logo.png'
                  alt=''
                  style={{
                    width: 16,
                    height: 16,
                    marginRight: 6,
                    verticalAlign: 'middle',
                    objectFit: 'contain',
                  }}
                />
              )}
              {rs.rule_set_name}
              {rs.is_legaltech_library && (
                <span style={{ color: '#cf1322', marginLeft: 4, fontSize: 12 }}>律果規則庫</span>
              )}
              {rs.is_default && (
                <span style={{ color: '#52c41a', marginLeft: 8, fontSize: 12 }}>預設</span>
              )}
              <span style={{ color: '#9ca3af', marginLeft: 4, fontSize: 12 }}>
                （{rs.rules_count} 條）
              </span>
            </Select.Option>
          ))}
      </Select>

      {groups.map((g, i) => (
        <div
          key={i}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            padding: '14px 14px 10px',
            background: '#fafafa',
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <ExperimentOutlined style={{ color: '#6B7B5A', marginRight: 6 }} />
            <Text strong style={{ fontSize: 14 }}>
              測試{CN_NUMS[i]}
            </Text>
            {groups.length > 1 && (
              <Button
                type='text'
                size='small'
                icon={<CloseOutlined />}
                style={{ marginLeft: 'auto', color: '#9ca3af' }}
                onClick={() => removeGroup(i)}
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <FileSlot
              label='法務標準答案'
              accept='.docx'
              hint='.docx'
              file={g.legalDoc}
              onFile={f => setFile(i, 'legalDoc', f)}
              onClear={() => clearFile(i, 'legalDoc')}
            />
            <FileSlot
              label='待測文件'
              accept='.pdf,.docx'
              hint='.pdf / .docx'
              file={g.originalFile}
              onFile={f => setFile(i, 'originalFile', f)}
              onClear={() => clearFile(i, 'originalFile')}
            />
          </div>
        </div>
      ))}

      <Button
        icon={<PlusOutlined />}
        block
        disabled={groups.length >= MAX_GROUPS}
        onClick={addGroup}
        style={{ borderStyle: 'dashed' }}
      >
        新增一組（{groups.length}/{MAX_GROUPS}）
      </Button>

      <Button size='large' block disabled={!allReady} onClick={handleSubmit} style={BTN_STYLE}>
        開始批次驗測
      </Button>
    </Space>
  );

  // ── processing ──
  const renderProcessing = () => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <Spin size='large' />
      <div style={{ marginTop: 16, color: '#6b7280', fontSize: 14 }}>處理中，請耐心等候...</div>
      <div style={{ marginTop: 8, color: '#9ca3af', fontSize: 12 }}>共 {groups.length} 組</div>
    </div>
  );

  // ── done ──
  const renderDone = () => {
    const columns = [
      {
        title: '檔名',
        dataIndex: 'name',
        key: 'name',
        width: 220,
        render: (name: string, _: BatchGroupResult, index: number) => {
          const meta = groupMetas[index];
          return (
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
              {meta?.originalName && (
                <div
                  style={{ fontSize: 11, color: '#6b7280', marginTop: 2, wordBreak: 'break-all' }}
                >
                  {meta.originalName}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: '正確率',
        dataIndex: 'recall',
        key: 'recall',
        width: 80,
        render: (v: number) => (
          <Text strong style={{ color: '#166534' }}>
            {v.toFixed(1)}%
          </Text>
        ),
      },
      {
        title: '誤報率',
        dataIndex: 'false_positive_rate',
        key: 'fpr',
        width: 80,
        render: (v: number) => (
          <Text strong style={{ color: '#991b1b' }}>
            {v.toFixed(1)}%
          </Text>
        ),
      },
      {
        title: '下載',
        key: 'dl',
        width: 140,
        render: (_: any, row: BatchGroupResult) => (
          <Space size='small'>
            <Button
              size='small'
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadGroup(result!.batch_id, row.idx, 'word')}
              style={{ ...BTN_STYLE, height: 28, fontSize: 12 }}
            >
              Word
            </Button>
            <Button
              size='small'
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadGroup(result!.batch_id, row.idx, 'excel')}
              style={{ ...BTN_STYLE, height: 28, fontSize: 12 }}
            >
              Excel
            </Button>
          </Space>
        ),
      },
    ];

    return (
      <Space direction='vertical' style={{ width: '100%' }} size='middle'>
        <Alert type='success' message='批次驗測完成！' showIcon />
        <Table
          dataSource={result?.groups ?? []}
          columns={columns}
          rowKey='idx'
          pagination={false}
          size='small'
          bordered
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 4, alignItems: 'center' }}>
          <Button size='middle' icon={<ArrowLeftOutlined />} onClick={reset}>
            返回
          </Button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
            <Button
              icon={<DownloadOutlined />}
              size='large'
              style={{ minWidth: 130, ...BTN_STYLE }}
              onClick={handleDownloadCombined}
            >
              統計表 Excel
            </Button>
            <Button size='large' style={{ minWidth: 130 }} icon={<ReloadOutlined />} onClick={handleSubmit}>
              重新審閱
            </Button>
          </div>
        </div>
      </Space>
    );
  };

  // ── error ──
  const renderError = () => (
    <Space direction='vertical' style={{ width: '100%' }} size='middle'>
      <Alert type='error' message='批次驗測失敗' description={errorMessage} showIcon />
      <Button size='large' block onClick={reset}>
        重試
      </Button>
    </Space>
  );

  const bodyMap: Record<Step, () => React.ReactNode> = {
    idle: renderIdle,
    processing: renderProcessing,
    done: renderDone,
    error: renderError,
  };

  return (
    <Modal
      title='批次驗測'
      open={visible}
      onCancel={handleClose}
      footer={null}
      closable={step !== 'processing'}
      maskClosable={step !== 'processing'}
      width={600}
      destroyOnClose
      afterClose={reset}
    >
      {bodyMap[step]()}
    </Modal>
  );
};

export default BatchValidationModal;
